import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import api from '@/services/api';
import { connectWebSocket, disconnectWebSocket } from '@/services/websocket';

let initialization = null;
let contextRevision = 0;

export default createStore({
  state: {
    isLoggedIn: false,
    authToken: null,
    user: null,
    companies: [],
    context: null,
  },
  getters: {
    hasPermission: (state) => (permission) => state.context?.permissions?.includes(permission),
    hasRole: (state) => (roleCode) => state.context?.roles?.some((role) => role.code === roleCode),
    activeCompany: (state) => state.context?.company || null,
    activeBranch: (state) => state.context?.branch || null,
    modules: (state) => state.context?.modules || [],
    hasModule: (state) => (moduleCode) => (state.context?.modules || []).some((module) => module.code === moduleCode),
  },
  mutations: {
    setSession(state, payload) {
      state.authToken = payload.auth_token || payload.authToken || state.authToken;
      state.user = payload.user || state.user;
      state.companies = payload.companies || state.companies;
      if (Object.prototype.hasOwnProperty.call(payload, 'context')) state.context = payload.context;
      state.isLoggedIn = !!state.authToken;
    },
    setContext(state, context) {
      contextRevision++;
      state.context = context;
    },
    setCompanies(state, companies) {
      state.companies = companies || [];
    },
    clearSession(state) {
      contextRevision++;
      state.isLoggedIn = false;
      state.authToken = null;
      state.user = null;
      state.companies = [];
      state.context = null;
    },
  },
  actions: {
    async initializeApp({ state, commit }) {
      if (!state.authToken) return;
      const token = state.authToken, revision = contextRevision;
      if (initialization?.token === token && initialization.revision === revision) return initialization.promise;
      const job = { token, revision, promise: null };
      job.promise = (async () => {
        try {
          const { data } = await api.get('/api/auth/me', { timeout: 5000 });
          if (state.authToken !== token || contextRevision !== revision) return;
          commit('setSession', data);
          if (data.context) connectWebSocket(data.context, data.user);
          return data;
        } catch (error) {
          if (state.authToken === token && contextRevision === revision && [401, 403].includes(error.response?.status)) {
            commit('clearSession'); disconnectWebSocket();
          } else if (!state.authToken && [401, 403].includes(error.response?.status)) {
            disconnectWebSocket();
          }
        } finally {
          if (initialization === job) initialization = null;
        }
      })();
      initialization = job;
      return job.promise;
    },
    async login({ commit }, credentials) {
      contextRevision++;
      const { data } = await api.post('/api/auth/login', credentials);
      commit('setSession', data);
      if (data.context) connectWebSocket(data.context, data.user);
      return data;
    },
    async register({ commit }, form) {
      contextRevision++;
      const { data } = await api.post('/api/auth/register', form);
      commit('setSession', data);
      if (data.context) connectWebSocket(data.context, data.user);
      return data;
    },
    async selectCompany({ commit, state }, payload) {
      const { data } = await api.post('/api/context/select-company', payload);
      commit('setContext', data.context);
      if (data.context) connectWebSocket(data.context, state.user);
      return data;
    },
    async logout({ commit }) {
      try {
        await api.post('/api/auth/logout');
      } catch (_error) {
        // Local logout still matters if the server session is gone.
      }
      disconnectWebSocket();
      commit('clearSession');
    },
  },
  plugins: [
    createPersistedState({
      paths: ['isLoggedIn', 'authToken', 'user', 'companies', 'context'],
    }),
  ],
});
